import type { AdminReport, AdminReportsView, ReportReason } from '@gardener/protocol';
import { createResource, createSignal, For, Match, Show, Switch } from 'solid-js';
import { call } from './api';
import { go } from './nav';
import {
  ago,
  ask,
  CARD_COLUMNS,
  EmptyState,
  Icon,
  LoadingLine,
  optimistic,
  TOOLBAR_ICON_CONTROL,
  Toolbar,
  ToolbarGroup,
  toast,
  Well,
  when,
} from './ui';

/** What each reason reads as, and its badge (logdash's `Badge` variants). */
const REASONS: Record<ReportReason, [string, 'neutral' | 'inverse' | 'error' | 'warning']> = {
  chat: ['Chat', 'error'],
  name: ['Name', 'warning'],
  cheating: ['Cheating', 'inverse'],
  other: ['Other', 'neutral'],
};

/** A name that opens the player's page. */
const LINK =
  'focus-visible:outline-brand cursor-pointer rounded-sm hover:underline focus-visible:outline-2 focus-visible:outline-offset-2';

/**
 * Reports (ADR-0156): what players reported about each other, the newest first: who, why, their note and the chat
 * between them as it was (what was written, before the word filter). An open one is dismissed, or its player banned
 * (which closes every open report about them); the closed ones say who closed them and how. Answer within a day: the
 * App Store asks for it.
 */
export function Reports() {
  const [open, setOpen] = createSignal<'open' | 'closed'>('open');
  const [list, { mutate, refetch }] = createResource(open, (o) =>
    call<AdminReportsView>('GET', `/api/admin/reports?open=${o === 'open'}`),
  );
  // A resource read while it failed throws (and takes the app down): ask for its error first.
  const loaded = () => (list.error ? undefined : list());
  /** Takes reports out of the open list (closed meanwhile); returns how to put them back. */
  const drop = (gone: (r: AdminReport) => boolean): (() => void) => {
    const before = loaded();
    if (before) mutate({ reports: before.reports.filter((r) => !gone(r)) });
    return () => mutate(before);
  };

  async function dismiss(r: AdminReport) {
    await optimistic(
      () => drop((x) => x.id === r.id),
      () => call('POST', `/api/admin/reports/${encodeURIComponent(r.id)}/dismiss`),
      () => toast(`The report about ${r.about.name} is dismissed`),
    );
  }

  async function ban(r: AdminReport) {
    const reason = await ask({
      title: `Ban ${r.about.name}?`,
      body: 'Their game closes at once, the Discord and Twitch they linked are banned with them, and every open report about them closes.',
      input: { label: 'Reason (kept with the ban)', placeholder: `Reported: ${REASONS[r.reason][0].toLowerCase()}` },
      confirm: 'Ban',
      danger: true,
    });
    if (!reason) return;
    await optimistic(
      () => drop((x) => x.about.id === r.about.id),
      () => call('POST', `/api/admin/players/${encodeURIComponent(r.about.id)}/ban`, { reason }),
      () => toast(`${r.about.name} is banned`),
    );
  }

  return (
    <div class="@container flex w-full flex-col">
      <Toolbar>
        <ToolbarGroup
          label="Reports"
          value={open()}
          options={[
            ['open', 'Open'],
            ['closed', 'Closed'],
          ]}
          onChange={setOpen}
        />
        <button
          type="button"
          class={TOOLBAR_ICON_CONTROL}
          title="Refresh"
          aria-label="Refresh"
          onClick={() => refetch()}
        >
          <Icon name="refresh" class={`size-3.5 ${list.loading ? 'animate-spin' : ''}`} />
        </button>
      </Toolbar>
      <div class="flex flex-col gap-2 p-2">
        <For
          each={loaded()?.reports ?? []}
          fallback={
            <Well label="Reports">
              <Switch
                fallback={
                  <EmptyState
                    centered
                    title={open() === 'open' ? 'Nothing to read' : 'No reports yet'}
                    description={
                      open() === 'open'
                        ? 'No open reports. New ones show up here as players send them.'
                        : 'Reports you dismiss, or close with a ban, are kept here.'
                    }
                  />
                }
              >
                <Match when={list.loading}>
                  <div class="px-3 py-2.5">
                    <LoadingLine label="Loading reports" />
                  </div>
                </Match>
                <Match when={list.error}>
                  {/* logdash's failed list (`TeamManagementCard`): a muted line after a danger sign. */}
                  <div class="text-fg-muted flex items-center gap-2 px-3 py-2.5 text-[13px]" role="alert">
                    <Icon name="danger" class="size-4 shrink-0" />
                    {(list.error as Error).message}
                  </div>
                </Match>
              </Switch>
            </Well>
          }
        >
          {(r) => <ReportCard r={r} dismiss={dismiss} ban={ban} />}
        </For>
      </div>
    </div>
  );
}

/** `[2026-10-10 14:12:56]` in this browser's time zone, as logdash's log rows stamp a line: [day, time]. */
const stamp = (iso: string): string[] => new Date(iso).toLocaleString('sv-SE').split(' ');

/**
 * One report, a well: who was reported and why, by whom and when, then the actions (or how it was closed); the
 * reporter's note; the chat between them as logdash's log rows, the reported player's lines marked red.
 */
function ReportCard(props: { r: AdminReport; dismiss: (r: AdminReport) => void; ban: (r: AdminReport) => void }) {
  const r = () => props.r;
  const who = (id: string) => (id === r().from.id ? r().from.name : r().about.name);
  const [label, variant] = REASONS[r().reason];
  return (
    <Well label={`Report about ${r().about.name}`}>
      <header class="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2 py-0.5 pl-3">
        <div class="flex min-w-0 flex-1 basis-56 flex-col gap-1">
          <div class="flex min-w-0 items-center gap-2">
            <button
              type="button"
              class={`${LINK} min-w-0 truncate text-left text-sm font-medium`}
              onClick={() => go('players', r().about.id)}
            >
              {r().about.name}
            </button>
            <span class="ld-badge shrink-0" data-variant={variant} data-size="sm">
              {label}
            </span>
            <Show when={r().about.banned}>
              <span class="ld-badge shrink-0" data-variant="outline" data-size="sm">
                Banned
              </span>
            </Show>
          </div>
          <p class="text-fg-muted truncate text-xs">
            Reported by{' '}
            <button
              type="button"
              class={`${LINK} text-fg-tertiary hover:text-fg-default transition-ink`}
              onClick={() => go('players', r().from.id)}
            >
              {r().from.name}
            </button>{' '}
            <time datetime={r().at} title={when(r().at)}>
              {ago(r().at)}
            </time>
          </p>
        </div>
        <Show
          when={r().closed}
          fallback={
            <div class="flex shrink-0 items-center gap-2">
              <button type="button" class="ld-button" data-size="sm" onClick={() => props.dismiss(r())}>
                <Icon name="check" class="size-4" />
                Dismiss
              </button>
              <Show when={!r().about.banned}>
                <button
                  type="button"
                  class="ld-button"
                  data-size="sm"
                  data-variant="danger"
                  onClick={() => props.ban(r())}
                >
                  Ban {r().about.name}
                </button>
              </Show>
            </div>
          }
        >
          {(c) => (
            <span class="text-fg-muted flex shrink-0 items-center gap-2 pr-3 text-xs" title={when(c().at)}>
              <span
                class={`size-2 shrink-0 rounded-full ${c().action === 'banned' ? 'bg-error' : 'bg-idle'}`}
                aria-hidden="true"
              />
              {c().action === 'banned' ? 'Banned' : 'Dismissed'} by {c().by} {ago(c().at)}
            </span>
          )}
        </Show>
      </header>
      <Show when={r().note}>
        <div class="flex flex-col">
          <div class={CARD_COLUMNS}>
            <span>Note</span>
          </div>
          <p class="text-fg-secondary px-3 py-1 text-sm text-pretty">“{r().note}”</p>
        </div>
      </Show>
      <Show when={r().chat.length > 0}>
        <div class="flex flex-col">
          <div class={CARD_COLUMNS}>
            <span>Chat, as it was</span>
            <span class="tabular-nums">
              {r().chat.length} {r().chat.length === 1 ? 'message' : 'messages'}
            </span>
          </div>
          <ol class="flex flex-col">
            <For each={r().chat}>
              {(m) => {
                const theirs = m.from === r().about.id;
                const [day, time] = stamp(m.at);
                return (
                  <li class="flex min-h-7 w-full min-w-0 flex-wrap items-start gap-x-2.5 px-3 py-1 font-mono text-sm">
                    <span
                      class={`mt-1.5 size-2 shrink-0 rounded-full ${theirs ? 'bg-error' : 'bg-idle'}`}
                      aria-hidden="true"
                    />
                    <span class="text-fg-tertiary shrink-0 whitespace-nowrap tabular-nums" title={when(m.at)}>
                      [<span class="max-sm:hidden">{day}&nbsp;</span>
                      {time}]
                    </span>
                    <span class={`max-w-32 shrink-0 truncate ${theirs ? 'text-error-fg' : 'text-fg-muted'}`}>
                      {who(m.from)}
                    </span>
                    <span
                      class={`min-w-0 flex-1 basis-full pl-4.5 break-words whitespace-pre-wrap @md:basis-0 @md:pl-0 ${theirs ? '' : 'text-fg-tertiary'}`}
                    >
                      {m.text}
                    </span>
                  </li>
                );
              }}
            </For>
          </ol>
        </div>
      </Show>
    </Well>
  );
}
